import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Flame, Baby, Sparkles, Swords, Hand, Trophy, ArrowRight, Phone, MapPin, Check } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1611816153165-fed23698666d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const KIDS_IMG = "https://images.pexels.com/photos/7045594/pexels-photo-7045594.jpeg?auto=compress&cs=tinysrgb&w=940";
const SPAR_IMG = "https://images.unsplash.com/photo-1620123646588-b9117246a9d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
const BJJ_IMG = "https://images.unsplash.com/photo-1606059100110-4230429584fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

const PROGRAMS = [
  { id: "samourais", icon: Baby, title: "Petits Samouraïs", age: "3 à 6 ans",
    desc: "Motricité, confiance et discipline en s'amusant — parent-enfant.",
    img: KIDS_IMG, to: "/petits-samourais", cta: "Inscrire mon enfant", accent: "from-amber-500/30" },
  { id: "powerkick", icon: Sparkles, title: "PowerKick Her", age: "Femmes",
    desc: "Cardio-kickboxing 100 % femmes. 20 $/cours · 2×/sem · sessions 12 sem.",
    img: HERO_IMG, to: "/powerkick-her", cta: "Réserver ma place", accent: "from-rose-500/30" },
  { id: "kick", icon: Swords, title: "Kickboxing / Muay Thaï", age: "Ados & adultes",
    desc: "Conditionnement, technique de frappe et autodéfense, tous niveaux.",
    img: SPAR_IMG, to: "/adhesion", cta: "M'inscrire", accent: "from-red-500/30" },
  { id: "bjj", icon: Hand, title: "Jiu-Jitsu (BJJ / Can-Ryu)", age: "Ados & adultes",
    desc: "Grappling au sol et autodéfense — progresse à ton rythme.",
    img: BJJ_IMG, to: "/adhesion", cta: "M'inscrire", accent: "from-blue-500/30" },
  { id: "prive", icon: Trophy, title: "Cours privés & semi-privés", age: "Sur rendez-vous",
    desc: "Coaching personnalisé Kick ou Jiu-Jitsu — forfaits 6 ou 12 cours.",
    img: SPAR_IMG, to: "/reserver-prive", cta: "Réserver un privé", accent: "from-emerald-500/30" },
];

export default function Ete2026Landing() {
  useEffect(() => { document.title = "Été 2026 — Institut Impact Élite Martial"; }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-50 selection:bg-red-600/40" data-testid="ete-2026-landing">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-red-400 border border-red-800/60 rounded-full px-4 py-1.5 mb-6">
            <Flame size={14} /> Sessions d'été 2026 · Val-d'Or
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Cet été, deviens<br /><span className="text-red-500">plus fort</span> sur le tatami.
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            Kickboxing, Muay Thaï, Jiu-Jitsu, programmes enfants et cours pour femmes.
            Inscription et paiement <strong className="text-white">100 % en ligne</strong> — places limitées.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/cours-essai" data-testid="ete-hero-essai-btn"
              className="group inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-full px-7 py-4 text-sm uppercase tracking-wider transition-colors">
              Réserver mon cours d'essai — 19,99 $
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="#programmes" className="text-sm uppercase tracking-wider text-zinc-300 hover:text-white transition-colors border-b border-zinc-700 pb-1">
              Voir les programmes
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
            {["Tous niveaux", "Coachs certifiés", "Ambiance familiale", "Suivi de progression Kaizen"].map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5"><Check size={14} className="text-red-500" /> {b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMMES */}
      <section id="programmes" className="max-w-6xl mx-auto px-6 py-16 md:py-20">
        <div className="mb-10">
          <h2 className="text-lg md:text-2xl font-bold">Choisis ton programme</h2>
          <p className="text-sm text-zinc-400 mt-1">Clique pour t'inscrire et payer en ligne en quelques secondes.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROGRAMS.map((p) => {
            const Icon = p.icon;
            return (
              <Link key={p.id} to={p.to} data-testid={`ete-program-${p.id}`}
                className="group relative rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900/60 hover:border-red-700/60 transition-all hover:-translate-y-1">
                <div className="relative h-40 overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${p.accent} to-zinc-900/90`} />
                  <Icon size={28} className="absolute bottom-3 left-4 text-white" />
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-black/60 text-zinc-200 rounded-full px-2.5 py-1">{p.age}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg">{p.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed min-h-[40px]">{p.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-red-400 group-hover:gap-3 transition-all">
                    {p.cta} <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="border-t border-zinc-800 bg-gradient-to-b from-red-950/20 to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-black">Pas sûr du programme ?</h2>
          <p className="mt-3 text-zinc-300">Viens essayer pour 19,99 $ (ou 2 semaines illimitées à 120 $). On t'aide à trouver ce qui te convient.</p>
          <Link to="/cours-essai" data-testid="ete-cta-essai-btn"
            className="mt-7 inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold rounded-full px-8 py-4 text-sm uppercase tracking-wider transition-colors">
            Mon cours d'essai — 19,99 $ <ArrowRight size={18} />
          </Link>
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-zinc-400">
            <a href="tel:+18195279446" className="inline-flex items-center gap-2 hover:text-white"><Phone size={15} /> (819) 527-9446</a>
            <span className="inline-flex items-center gap-2"><MapPin size={15} /> 1218, 7ᵉ Rue, Val-d'Or, QC</span>
          </div>
        </div>
        <div className="text-center text-xs text-zinc-600 pb-8 tracking-wider uppercase">
          Institut Impact Élite Martial · Kaizen System
        </div>
      </section>
    </div>
  );
}
